import { useContactLink } from "@/utils/contact";
import { trackEvent } from "@/utils/tracking";
import { MessageCircleQuestion, ThumbsUp, ThumbsDown } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "./Reveal";

const FAQS = [
  {
    q: "Apa yang saya dapatkan setelah membeli?",
    a: "Anda mendapatkan akses ke program digital (audio/materi) langsung dari platform resmi penyedia. Detail isi program tercantum di halaman checkout resmi sebelum Anda membayar.",
  },
  {
    q: "Bagaimana cara mengakses produknya setelah bayar?",
    a: "Setelah pembayaran selesai di platform resmi penyedia, instruksi akses dikirim otomatis oleh penyedia — biasanya melalui email atau aplikasi mereka. Prosesnya cepat dan langsung bisa digunakan.",
  },
  {
    q: "Apakah ini produk fisik atau digital?",
    a: "Ini produk digital — tidak ada pengiriman barang. Anda bisa mengaksesnya kapan saja dari ponsel atau laptop. (Khusus parfum NALURA Essence yang sedang Open PO, itu produk fisik terpisah.)",
  },
  {
    q: "Berapa lama waktu yang saya perlukan setiap hari?",
    a: "Umumnya cukup 10–20 menit per hari, misalnya saat santai, sebelum tidur, atau saat perjalanan. Kuncinya bukan durasi panjang, melainkan konsistensi.",
  },
  {
    q: "Apakah cocok untuk pemula?",
    a: "Ya. Program ini dirancang agar mudah diikuti siapa saja tanpa pengalaman sebelumnya — tinggal dengarkan dan jalani secara rutin.",
  },
  {
    q: "Apakah transaksinya aman? Siapa yang memproses pembayaran?",
    a: "Aman. Seluruh pembayaran diproses oleh platform resmi penyedia program (bukan oleh NALURA), dengan sistem pembayaran terverifikasi. Kami hanya mengarahkan Anda ke halaman checkout resmi mereka.",
  },
  {
    q: "Bagaimana kalau saya sudah pernah coba program serupa dan tidak berhasil?",
    a: "Hasil setiap orang memang berbeda dan tidak ada yang bisa menjamin hasil pasti. Yang sering membedakan adalah konsistensi dan kecocokan pendekatan. Jika ragu, silakan tanya dulu lewat tombol di bawah — kami jawab jujur apakah program ini sesuai kebutuhan Anda.",
  },
];

const COCOK = [
  "Ingin membangun kebiasaan dan pola pikir secara bertahap",
  "Bersedia meluangkan 10–20 menit setiap hari dengan konsisten",
  "Terbuka pada pendekatan relaksasi dan pemrograman bawah sadar",
  "Mencari pendamping perjalanan pengembangan diri, bukan jalan pintas",
];

const TIDAK_COCOK = [
  "Mencari hasil instan dalam semalam — perubahan butuh proses",
  "Mengharapkan jaminan penghasilan atau hasil pasti — tidak ada yang bisa menjanjikan itu",
  "Membutuhkan nasihat keuangan, medis, atau psikologis profesional — program ini bukan penggantinya",
  "Tidak siap meluangkan waktu rutin untuk diri sendiri",
];

export const ConversionBlock = ({ product }) => {
  const contactLink = useContactLink(`Halo NALURA! Saya mau tanya dulu tentang ${product.name} sebelum membeli.`);

  return (
    <div className="mt-24 md:mt-32 max-w-4xl" data-testid="conversion-block">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#A67C1E] mb-6">Jujur Sejak Awal</p>
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          <div className="border border-[#1B4332]/25 bg-[#1B4332]/5 p-8" data-testid="fit-column">
            <p className="flex items-center gap-2 font-display text-xl tracking-tight mb-5">
              <ThumbsUp className="w-4 h-4 text-[#1B4332]" /> Produk ini cocok untuk kamu kalau...
            </p>
            <ul className="space-y-3 text-sm text-[#211B10]/75">
              {COCOK.map((c) => <li key={c} className="flex gap-2.5"><span className="text-[#1B4332]">✓</span><span>{c}</span></li>)}
            </ul>
          </div>
          <div className="border border-[#9B2C2C]/25 bg-[#9B2C2C]/5 p-8" data-testid="not-fit-column">
            <p className="flex items-center gap-2 font-display text-xl tracking-tight mb-5">
              <ThumbsDown className="w-4 h-4 text-[#9B2C2C]" /> Sebaliknya, ini bukan untuk kamu kalau...
            </p>
            <ul className="space-y-3 text-sm text-[#211B10]/75">
              {TIDAK_COCOK.map((c) => <li key={c} className="flex gap-2.5"><span className="text-[#9B2C2C]">✕</span><span>{c}</span></li>)}
            </ul>
          </div>
        </div>
      </Reveal>

      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#A67C1E] mb-6">Pertanyaan yang Sering Diajukan</p>
        <Accordion type="single" collapsible className="border border-[#211B10]/10 bg-white px-6 md:px-8" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-[#211B10]/10">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-base md:text-lg tracking-tight hover:text-[#A67C1E] hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#211B10]/65 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>

      {contactLink && (
        <Reveal className="mt-10">
          <a
            data-testid="ask-before-buy-button"
            href={contactLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("klik_kontak", { lokasi: "tanya-sebelum-beli", produk: product.name })}
            className="inline-flex items-center gap-3 border border-[#A67C1E]/50 text-[#A67C1E] font-medium text-sm uppercase tracking-[0.12em] px-8 py-4 hover:bg-[#A67C1E] hover:text-[#FBF7EE] transition-colors duration-300"
          >
            <MessageCircleQuestion className="w-4 h-4" /> Tanya dulu sebelum beli
          </a>
        </Reveal>
      )}
    </div>
  );
};
